import Vue from 'vue'
import VueRouter from 'vue-router'
import VueMeta from 'vue-meta'

import store from '@/state/store'
import routes from './routes'
import ssoService from '@/services/sso.service'

Vue.use(VueRouter)
Vue.use(VueMeta, {
  keyName: 'page',
})

const router = new VueRouter({
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'hash',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
})

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
  // Detect SSO parameters in URL
  const ssoParams = ssoService.detectSSOParams();
  if (ssoParams) {
    // Store SSO parameters in store for later use
    store.dispatch('auth/setSSOParams', ssoParams);
    
    // Validate SSO parameters
    const validation = ssoService.validateSSOParams(ssoParams);
    if (!validation.valid) {
      console.error('Invalid SSO parameters:', validation.errors);
      // Clean invalid parameters and continue normally
      ssoService.cleanSSOParamsFromURL();
      store.dispatch('auth/clearTempData');
    }
  }
  
  // Check if auth is required on this route
  // (including nested routes).
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired)

  // If auth isn't required for the route, just continue.
  if (!authRequired) return next();

  // If auth is required and the user is logged in...
  if (store.getters['auth/loggedIn']) {

    //Validate permissions
    const permission = routeTo.meta.permission;  
    if (permission) {

      const hasPermission = store.getters['permission/hasPermission'](permission);
      
      if (hasPermission) {
        return next(); // El usuario tiene el permiso
      } else {
        return next('/unauthorized'); // Redirigir si no tiene el permiso
      }
    }

    return next();
  }

  // If auth is required and the user is NOT currently logged in,
  // redirect to login.
  redirectToLogin()

  // eslint-disable-next-line no-unused-vars
  // eslint-disable-next-line no-inner-declarations
  function redirectToLogin() {
    // If we have SSO parameters, redirect to login with them preserved
    if (ssoParams) {
      // SSO login attempt - redirect to login with SSO context
      next({ 
        name: 'login', 
        query: { 
          redirectFrom: routeTo.fullPath,
          // Preserve SSO parameters in query for login component
          sso_token: ssoParams.sso_token,
          redirect_to: ssoParams.redirect_to,
          app_origin: ssoParams.app_origin,
          sso_action: ssoParams.sso_action
        } 
      });
    } else {
      // Normal login redirect
      next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
    }
  }
})

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  next()
})

// After each route navigation
router.afterEach((to, from) => {
  // Handle SSO logout propagation if coming from logout
  if (from.name === 'logout') {
    const ssoParams = store.state.auth.ssoParams;
    if (ssoParams && ssoParams.redirect_to) {
      // Perform SSO logout redirect after a short delay
      setTimeout(() => {
        const logoutURL = ssoService.buildSSOLogoutURL(window.location.origin, {
          redirect_to: ssoParams.redirect_to,
          app_origin: ssoParams.app_origin
        });
        
        try {
          ssoService.performRedirect(logoutURL);
        } catch (error) {
          console.error('SSO logout redirect failed:', error);
        }
      }, 1000);
    }
  }
  
  // Clear temporary SSO data after successful navigation (except for login)
  if (to.name !== 'login') {
    // Small delay to allow login component to process SSO params
    setTimeout(() => {
      if (!store.getters['auth/hasTempToken']) {
        store.dispatch('auth/clearTempData');
      }
    }, 100);
  }
})

export default router
